import { durationLabelOrNothing } from '@/lib/duration-label';
import type { ChatRoomTranscript } from '@matter_extractor/shared';
import type { ReactNode } from 'react';
import {
  ROOM_STATE_LABEL,
  TILE_BREAKS,
  TILE_CONSENTS,
  TILE_DURATION,
  TILE_MESSAGES,
  TILE_STATE,
} from './entretien-labels';

/**
 * The five figures at the head of S10 (chat spec §12).
 *
 * Five facts, and each is one an operator would otherwise count by hand off the
 * thread below: what happened to the room, how much was said, how long it ran,
 * whether both sides consented, and how many connection breaks the transcript
 * traces. Nothing derived beyond that - no rate, no average, no comparison to
 * another interview. §2 puts measurement out of scope for this dispositif
 * outright («  le transcript se lit à l'oeil »), and a tile carrying a score
 * would be the first inch of the report the spec refuses.
 *
 * « Coupures » is the one tile that changes colour, and only upward from zero: a
 * break is not a fault, it is a thing that happened to the conversation and
 * changes how the pauses below should be read. Zero prints in the same ink as
 * the rest, because « nothing happened » is not news.
 */

export interface TranscriptHeadProps {
  transcript: ChatRoomTranscript;
}

export function TranscriptHead({ transcript }: TranscriptHeadProps) {
  const { room } = transcript;
  return (
    <div
      data-testid="chat-transcript-head"
      /*
        The maquette's hairline grid, drawn with BORDERS on the tiles rather than
        with a `gap-px` over a grey ground. The ground would have been the
        shorter spelling and the charte bans it - `gray-200` and darker is never
        a surface, white is the ground - so the seams are borders and the
        container supplies the two edges the tiles cannot: each tile paints its
        own top and left, the container its right and bottom.

        Two columns on a phone and five from `sm` up: five 20 % columns at 390 px
        would put « Consentements » on four lines.
      */
      className="grid grid-cols-2 overflow-hidden rounded-md border-gray-200 border-r border-b sm:grid-cols-5"
    >
      <Tile label={TILE_STATE} value={ROOM_STATE_LABEL[room.state]} />
      <Tile label={TILE_MESSAGES} value={room.messageCount} numeric />
      <Tile label={TILE_DURATION} value={durationLabelOrNothing(room.durationMinutes)} numeric />
      {/*
        A COUNT and not the two hours: §6 keeps a refusal invisible to the other
        SIDE - which an operator is not, since she holds the room to person
        correspondence - but there is still nothing this screen does with the
        minute somebody ticked a box, and « 1 / 2 » is the whole of what the
        figure has to say.
      */}
      <Tile
        label={TILE_CONSENTS}
        value={`${transcript.consentedCount} / ${transcript.participantCount}`}
        numeric
      />
      <Tile
        label={TILE_BREAKS}
        value={transcript.breakCount}
        numeric
        tone={transcript.breakCount > 0 ? 'warning' : 'plain'}
      />
    </div>
  );
}

function Tile({
  label,
  value,
  numeric,
  tone = 'plain',
}: {
  label: string;
  value: ReactNode;
  numeric?: boolean;
  tone?: 'plain' | 'warning';
}) {
  return (
    <div className="space-y-1 border-gray-200 border-t border-l bg-gray-50 px-4 py-3">
      <p className="font-semibold text-gray-600 text-xs uppercase tracking-wide">{label}</p>
      <p
        className={[
          'font-semibold text-xl',
          numeric === true ? 'tabular-nums' : '',
          tone === 'warning' ? 'text-jaune-deep' : 'text-gray-900',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </p>
    </div>
  );
}
